import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { authClient } from '../../utils/amplifyClient';
import * as Sentry from '@sentry/react';
import { useUserContext } from '../../contexts/UserContext';
import { useEquipmentContext } from '../../contexts/EquipmentContext';
import { useRegionAndLocationContext } from '../../contexts/RegionAndLocationContext';
import { useAppContext } from '../../contexts/AppContext';
import { InboxNotificationItem, InboxFilter } from './inboxTypes';
import { InboxListItem } from './InboxListItem';
import { InboxDetailPanel } from './InboxDetailPanel';
import { buildInboxNotifications } from './inboxUtils';
import '../../css/InboxContent.css';
import { Btn, PageHeader, TEIQspinner } from '../ui';
import { CustomModal } from '../CustomModal';

const INBOX_READ_STORAGE_KEY = 'inboxReadIds';

function getStoredReadIds(userId: string): Set<string> {
    try {
        const stored = localStorage.getItem(`${INBOX_READ_STORAGE_KEY}_${userId}`);
        if (stored) return new Set(JSON.parse(stored));
    } catch {
        /* ignore parse errors */
    }
    return new Set();
}

function persistReadIds(userId: string, readIds: Set<string>) {
    try {
        localStorage.setItem(`${INBOX_READ_STORAGE_KEY}_${userId}`, JSON.stringify([...readIds]));
    } catch {
        /* ignore storage errors */
    }
}

export const InboxContent = () => {
    const navigate = useNavigate();
    const { company, companyUser, companyOwner } = useUserContext();
    const { companyUnits } = useEquipmentContext();
    const { companyLocations } = useRegionAndLocationContext();
    const { setInboxCount } = useAppContext();

    const [notifications, setNotifications] = useState<InboxNotificationItem[]>([]);
    const [selectedNotification, setSelectedNotification] = useState<InboxNotificationItem | null>(null);
    const [activeFilter, setActiveFilter] = useState<InboxFilter>('all');
    const [isLoading, setIsLoading] = useState(true);
    const [showMarkAllReadModal, setShowMarkAllReadModal] = useState(false);

    const companyId = company?.id || companyOwner?.companyId || '';
    const userId = companyUser?.id || companyOwner?.id || '';

    const [readIds, setReadIds] = useState<Set<string>>(() => getStoredReadIds(userId));

    useEffect(() => {
        if (companyId) {
            loadNotifications();
            return;
        }
        setIsLoading(false);
        setNotifications([]);
    }, [companyId]);

    // Re-load persisted readIds when userId becomes available
    useEffect(() => {
        if (userId) {
            setReadIds(getStoredReadIds(userId));
        }
    }, [userId]);

    // Sync inbox count with AppContext whenever notifications or readIds change
    useEffect(() => {
        if (notificationList.length > 0) {
            const newUnread = notificationList.filter((n) => n?.id && !readIdSet.has(n.id)).length;
            setInboxCount(newUnread);
        }
    }, [notifications, readIds]);

    const loadNotifications = async () => {
        setIsLoading(true);
        try {
            const items = await buildInboxNotifications(authClient, companyId, companyUnits, companyLocations);
            setNotifications(items);
            if (items.length > 0 && !selectedNotification) {
                setSelectedNotification(items[0]);
            }
        } catch (error) {
            console.error('Error loading inbox notifications:', error);
            Sentry.captureException(error);
        } finally {
            setIsLoading(false);
        }
    };

    const notificationList = Array.isArray(notifications) ? notifications : [];
    const readIdSet = readIds instanceof Set ? readIds : new Set<string>();

    const unreadCount = useMemo(() => {
        return notificationList.filter((n) => n?.id && !readIdSet.has(n.id)).length;
    }, [notificationList, readIdSet]);

    const filteredNotifications = useMemo(() => {
        const withId = notificationList.filter((n) => Boolean(n?.id));
        switch (activeFilter) {
            case 'unread':
                return withId.filter((n) => !readIdSet.has(n.id));
            case 'alerts':
                return withId.filter((n) => n.type === 'critical-alert' || n.type === 'failed-inspection');
            case 'assigned':
                return withId.filter((n) => n.type === 'inspection-assigned' || n.type === 'pm-due');
            default:
                return withId;
        }
    }, [notificationList, activeFilter, readIdSet]);

    const handleSelectNotification = (notification: InboxNotificationItem) => {
        setSelectedNotification(notification);
        setReadIds((prev) => {
            const updated = new Set([...prev, notification.id]);
            persistReadIds(userId, updated);
            // Update nav badge: total notifications minus read ones
            const newUnread = notifications.filter((n) => !updated.has(n.id)).length;
            setInboxCount(newUnread);
            return updated;
        });
    };

    const handleMarkAllRead = () => {
        const allIds = new Set(notifications.map((n) => n.id));
        setReadIds(allIds);
        persistReadIds(userId, allIds);
        setInboxCount(0);
        setShowMarkAllReadModal(false);
    };

    const filters: { key: InboxFilter; label: string; count?: number }[] = [
        { key: 'all', label: 'All', count: notificationList.length },
        { key: 'unread', label: 'Unread', count: unreadCount },
        { key: 'alerts', label: 'Alerts' },
        { key: 'assigned', label: 'Assigned' },
    ];

    return (
        <div className="page-container inbox-container">
            {/* Header */}
            <PageHeader
                title="Inbox"
                signalLine="System notifications, alerts, and assignments. Notifications are auto-generated by inspections, PM schedules, and reported issues."
                actions={
                    <>
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Mark All Read"
                            buttonDisabled={unreadCount === 0}
                            buttonOnClick={() => setShowMarkAllReadModal(true)}
                        />
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Notification Settings"
                            buttonOnClick={() => navigate('/settings')}
                        />
                    </>
                }
            />

            {/* Main content */}
            <div className="inbox-body">
                {/* Left panel - notification list */}
                <div className="inbox-list-panel">
                    {/* Filter tabs */}
                    <div className="inbox-filters">
                        {filters.map((filter) => (
                            <button
                                key={filter.key}
                                className={`inbox-filter-btn ${activeFilter === filter.key ? 'active' : ''}`}
                                onClick={() => setActiveFilter(filter.key)}
                            >
                                {filter.label}
                                {filter.count !== undefined && (
                                    <span className="inbox-filter-count">{filter.count}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Notification list */}
                    <div className="inbox-list">
                        {isLoading ? (
                            <div className="inbox-loading">
                                <TEIQspinner variant="md" label="Loading notifications" />
                            </div>
                        ) : filteredNotifications.length === 0 ? (
                            <div className="inbox-empty">
                                <p>No notifications</p>
                            </div>
                        ) : (
                            filteredNotifications.map((notification) => (
                                <InboxListItem
                                    key={notification.id}
                                    notification={notification}
                                    isSelected={selectedNotification?.id === notification.id}
                                    isRead={readIds.has(notification.id)}
                                    onClick={() => handleSelectNotification(notification)}
                                />
                            ))
                        )}
                    </div>
                </div>

                {/* Right panel - detail view */}
                <div className="inbox-detail-panel">
                    {selectedNotification ? (
                        <InboxDetailPanel notification={selectedNotification} />
                    ) : (
                        <div className="inbox-detail-empty">
                            <p>Select a notification to view details</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Mark All Read Modal */}
            <CustomModal
                visible={showMarkAllReadModal}
                onDismiss={() => setShowMarkAllReadModal(false)}
                header="Mark All as Read?"
                footer={
                    <div className="modal-footer-actions">
                        <Btn
                            variant="secondary-outlined"
                            buttonText="Cancel"
                            buttonOnClick={() => setShowMarkAllReadModal(false)}
                        />
                        <Btn variant="primary" buttonText="Mark All Read" buttonOnClick={handleMarkAllRead} />
                    </div>
                }
            >
                <p>
                    This will mark all <strong>{unreadCount} notifications</strong> in your inbox as read. They will
                    remain in your inbox and can still be viewed.
                </p>
            </CustomModal>
        </div>
    );
};
